import React, { useEffect, useState } from 'react';
import { View, Image, StyleSheet, TouchableOpacity, ActivityIndicator, Text, Platform, Alert, Modal } from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '@/config/theme';
import { useWebAlert, AlertButton } from '@/components/WebAlert';
import MarkPad from '@/components/checklist/MarkPad';
import { APP_CONSTANTS } from '@/config/constants';
import { supabase } from '@/services/supabase';
import { uploadDevChecklistImage, deleteDevChecklistImage } from '@/services/dev/devChecklistImageService';
import { DevChecklistEditorLogger } from '@/utils/logger';

interface DevChecklistMarkFieldProps {
  empresaId: string;
  checklistId: string;
  fieldId: string;
  value?: string;
  onChange: (uri: string) => void;
  readOnly?: boolean;
}

const logger = DevChecklistEditorLogger.Image;

const DevChecklistMarkField: React.FC<DevChecklistMarkFieldProps> = ({
  empresaId,
  checklistId,
  fieldId,
  value,
  onChange,
  readOnly = false,
}) => {
  const [backgroundUri, setBackgroundUri] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { showAlert } = useWebAlert();

  const showMessage = (title: string, msg: string, buttons?: AlertButton[]) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg, buttons ?? [{ text: 'OK' }]);
    }
  };

  useEffect(() => {
    let mounted = true;
    const loadBackground = async () => {
      try {
        const { data, error } = await supabase
          .from(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME)
          .select('foto_url')
          .eq('empresa_id', empresaId)
          .eq('campo_id', fieldId)
          .limit(1)
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        if (mounted) {
          setBackgroundUri(data?.foto_url ?? null);
        }
      } catch (err) {
        logger.warn('[DevChecklistMarkField] Falha ao carregar imagem de fundo', err);
        if (mounted) {
          setBackgroundUri(null);
        }
      }
    };

    loadBackground();
    return () => {
      mounted = false;
    };
  }, [empresaId, fieldId]);

  const handleSelectedImage = async (dataUrl: string) => {
    if (readOnly) {
      showMessage('Somente leitura', 'Alterações de marcação estão temporariamente desabilitadas.');
      return;
    }

    setSaving(true);
    try {
      const result = await uploadDevChecklistImage({
        empresaId,
        checklistId,
        campoId: fieldId,
        localUri: dataUrl,
        kind: 'marcacao',
      });
      onChange(result.publicUrl);
      showMessage('Sucesso', 'Marcação salva com sucesso!');
    } catch (error: any) {
      logger.warn('[DevChecklistMarkField] Falha ao salvar marcação', error);
      showMessage('Erro', error?.message || 'Falha ao salvar a marcação.');
    } finally {
      setSaving(false);
      setModalOpen(false);
    }
  };

  const handleRemoveMark = () => {
    if (readOnly) {
      showMessage('Somente leitura', 'Remoções de marcação estão temporariamente desabilitadas.');
      return;
    }

    const confirmRemove = () => {
      showMessage(
        'Confirmar remoção',
        'Tem certeza que deseja remover esta marcação?',
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Remover', style: 'destructive', onPress: performRemove },
        ]
      );
    };

    const performRemove = async () => {
      setDeleting(true);
      try {
        await deleteDevChecklistImage(value);
        onChange('');
        showMessage('Sucesso', 'Marcação removida com sucesso!');
      } catch (error: any) {
        logger.warn('[DevChecklistMarkField] Falha ao remover marcação', error);
        showMessage('Erro', error?.message || 'Falha ao remover a marcação.');
      } finally {
        setDeleting(false);
      }
    };

    confirmRemove();
  };

  return (
    <View style={styles.container}>
      {value ? (
        <>
          <Image source={{ uri: value }} style={styles.preview} />
          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.button}
              onPress={() => setModalOpen(true)}
              disabled={readOnly || saving || deleting}
            >
              {saving ? (
                <ActivityIndicator size="small" color={theme.colors.primary.default} />
              ) : (
                <Text style={styles.buttonText}>Editar marcação</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.buttonDanger]}
              onPress={handleRemoveMark}
              disabled={readOnly || saving || deleting}
            >
              {deleting ? (
                <ActivityIndicator size="small" color={theme.colors.util.error} />
              ) : (
                <Text style={[styles.buttonText, styles.buttonDangerText]}>Remover</Text>
              )}
            </TouchableOpacity>
          </View>
        </>
      ) : (
        <TouchableOpacity
          style={styles.emptyButton}
          onPress={() => setModalOpen(true)}
          disabled={readOnly || saving || !backgroundUri}
        >
          {saving ? (
            <ActivityIndicator size="small" color={theme.colors.primary.default} />
          ) : (
            <>
              <MaterialCommunityIcons name="brush" size={18} color={theme.colors.primary.default} />
              <Text style={styles.emptyButtonText}>
                {backgroundUri ? 'Adicionar marcação' : 'Sem imagem de fundo'}
              </Text>
            </>
          )}
        </TouchableOpacity>
      )}

      <Modal visible={modalOpen} transparent animationType="slide">
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContent}>
            {backgroundUri ? (
              <MarkPad
                backgroundUri={backgroundUri}
                onOK={handleSelectedImage}
                onClose={() => setModalOpen(false)}
              />
            ) : (
              <View style={styles.missingBackground}>
                <Text style={styles.missingBackgroundText}>Imagem de fundo indisponível.</Text>
              </View>
            )}
            <TouchableOpacity style={styles.modalClose} onPress={() => setModalOpen(false)}>
              <Ionicons name="close" size={20} color={theme.colors.text.primary} />
              <Text style={styles.modalCloseText}>Fechar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing.sm,
  },
  preview: {
    width: '100%',
    height: 220,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
  },
  actions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  button: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: '500' as any,
  },
  buttonDanger: {
    borderColor: theme.colors.util.error,
  },
  buttonDangerText: {
    color: theme.colors.util.error,
  },
  emptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.background.primary,
  },
  emptyButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: '500' as any,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.65)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.md,
  },
  modalContent: {
    width: '100%',
    maxWidth: 720,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
  },
  modalClose: {
    marginTop: theme.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
  },
  modalCloseText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  missingBackground: {
    padding: theme.spacing.md,
    alignItems: 'center',
  },
  missingBackgroundText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
});

export default DevChecklistMarkField;
